import { AccountBase } from '@/models/AccountViewModel';
import { AccountTag } from '@/store/accountsApi';

/**
 * Represents a single account object from the API.
 * Note the use of an index signature `[key: string]: any;` to allow for
 * dynamic keys like "goals:3", which are not part of a standard model.
 */
export interface Account {
  AccountId: number;
  Name: string;
  Balance: number;
  // ... other known, static properties
  Tags: AccountTag[];
  [key: string]: any; // This allows for dynamic keys, e.g., account['goals:3']
}

/**
 * Represents a single entry in our "dictionary" of all possible group categories.
 * This data is global and NOT specific to one account.
 */
export interface AccountGroupCategory {
  TagCategoryId: number;
  Name: string;
  IsAdvisorTag: boolean;
  IsEnabled: boolean;
  Description?: string;
  // Align with store/accountsApi shape for structural compatibility
  Tags: AccountTag[];
}

/**
 * Represents the final, clean output from our logic, ready to be rendered in the UI.
 */
export interface FormattedGroup {
  label: string;
  value: string;
}

/**
 * Formats account tags into a display-friendly array of labels and values.
 * It iterates over the keys of the `account.Tags` object, which contain
 * dynamic identifiers (e.g., "investor:68"), and matches them against the
 * global group dictionary.
 *
 * @param account The specific Account object.
 * @param allGroups The global dictionary of all possible AccountGroupCategory objects.
 * @returns An array of FormattedGroup objects ready for display.
 */
export const formatGroups = (
  account: AccountBase,
  allGroups: Array<{ TagCategoryId: number; Name: string }>,
): FormattedGroup[] => {
  // 1. If we don't have the necessary data, return early.
  if (!account?.Tags || !allGroups?.length) {
    return [];
  }

  const formattedGroups: FormattedGroup[] = [];
  const dynamicKeyRegex = /^(?:investor|advisor):([1-9]\d*)$/;

  // 2. Iterate over every key in the `account.Tags` object.
  for (const key in account.Tags) {
    // 3. Check if the key is a direct property of the object and matches our dynamic pattern.
    if (Object.prototype.hasOwnProperty.call(account.Tags, key)) {
      const match = key.match(dynamicKeyRegex);

      if (match) {
        // 4. If it matches, extract the numeric ID from the key.
        // For "investor:68", the `id` will be "68".
        const id = match[1]; // Note: The regex now has one capture group.

        // 5. Find the corresponding category object in our global dictionary.
        const groupDefinition = allGroups.find(
          (group) => group.TagCategoryId.toString() === id,
        );

        // 6. If we found a definition, we have a complete pair.
        if (groupDefinition) {
          // 7. Create a clean object and add it to our results.
          formattedGroups.push({
            label: groupDefinition.Name, // The LABEL (e.g., "Goals")
            value: account.Tags[key], // The VALUE (e.g., "Cash / Other / No Goal")
          });
        }
      }
    }
  }

  return formattedGroups;
};
